import { inject, Injectable } from '@angular/core';
import { Store } from '@fc-core/store-utils/store';
import { EventHistoryItem } from '@fc-core/notifications/models/event-history-item';
import { NotificationsService } from '@fc-core/notifications/services/notifications.service';

export interface NotificationsState {
  events: EventHistoryItem[];
  next: string | null;
  loading: boolean;
  loadingMore: boolean;
  selectedTab: string;
}

const initialState: NotificationsState = {
  events: [],
  next: null,
  loading: false,
  loadingMore: false,
  selectedTab: 'all',
};

@Injectable({ providedIn: 'root' })
export class NotificationsStore extends Store<NotificationsState> {
  api = inject(NotificationsService);

  constructor() {
    super(initialState);
  }

  async loadEvents(): Promise<void> {
    this.update({ loading: true, next: null }, 'loadEvents start');
    const response = await this.api.getEventsHistory(
      null,
      this.state().selectedTab,
    );
    this.update(
      { events: response.events, next: response.next, loading: false },
      'loadEvents done',
    );
  }

  async loadMoreEvents(): Promise<void> {
    const next = this.state().next;
    if (!next || this.state().loadingMore) return;
    this.update({ loadingMore: true }, 'loadMoreEvents start');
    const response = await this.api.getEventsHistory(
      next,
      this.state().selectedTab,
    );
    this.update(
      {
        events: [...this.state().events, ...response.events],
        next: response.next,
        loadingMore: false,
      },
      'loadMoreEvents done',
    );
  }

  async markAsRead(eventId: string): Promise<void> {
    const updatedEvent = await this.api.markAsRead(eventId);
    this.draftUpdate((draft) => {
      draft.events = draft.events.map((event) =>
        event.id === updatedEvent.id ? updatedEvent : event,
      );
    });
  }

  async markAllAsRead(): Promise<void> {
    const ids = this.state()
      .events.filter((event) => !event.read)
      .map((event) => event.id);
    if (!ids.length) return;
    const updatedIds = await this.api.markAllAsRead(ids);
    this.draftUpdate((draft) => {
      draft.events = draft.events.map((event) => {
        if (updatedIds && updatedIds.includes(event.id)) {
          return { ...event, read: true };
        }
        return event;
      });
    });
  }

  reset(): void {
    this.update(initialState);
  }

  selectTab(tab: string) {
    this.update({ selectedTab: tab, next: null, events: [] }, 'selectTab');
  }
}
